import { useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import * as Portal from '@radix-ui/react-portal';
import useEnrichmentAccess from '../../hooks/useEnrichmentAccess';
import useModalA11y from '../../hooks/useModalA11y';
import EnrichmentCreditCounter from '../common/EnrichmentCreditCounter';
import { getUsageColor } from '../../utils/constants/subscription';
import {
  EMAIL_COST,
  PHONE_COST,
  computeRevealCost
} from '../profiler/CompanyDetails/KeyContacts/keyContactsCostUtils';

const TITLE_ID = 'enrichment-download-dialog-title';
const INSUFFICIENT_MSG_ID = 'enrichment-download-dialog-insufficient';

// Per-field credit cost is sourced from the single source of truth in
// keyContactsCostUtils (EMAIL_COST / PHONE_COST), shared with the reveal cost
// math, rather than re-declaring it here.
const STATUS_FOUND = 'found';
const STATUS_NOT_FOUND = 'not_found';
const CACHED_CHARGED_AGAIN = 'cached, charged again';

const pluralCredits = (n) => `${n} ${n === 1 ? 'credit' : 'credits'}`;
const pluralUnit = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Count contacts whose persisted enrichment status is already resolved for a
// field. 'found' (normalised from BE 'success' at ingestion) AND 'not_found'
// both count as cached: each is a terminal verdict the BE charges 0 for
// (TD-2302), so they belong in the "already cached" total — keeping the cached
// indicator + skip math in agreement with computeRevealCost in the dropdown.
const countCachedForField = (contacts, fieldKey) =>
  (contacts || []).reduce((n, c) => {
    const status = c?.[`enrichment_status_${fieldKey}`];
    return n + (status === STATUS_FOUND || status === STATUS_NOT_FOUND ? 1 : 0);
  }, 0);

// The credit-counter stat-box palette is green / yellow / grey (no red). Map
// the shared getUsageColor 'red' tier onto 'grey' so the Company-downloads box
// reuses the exact stat-box classes without an unstyled fallback.
const toBoxColor = (color) => (color === 'red' ? 'grey' : color);

// ---------------------------------------------------------------------------
// Pure derivations. Extracted from the component so each stays small and the
// component's cognitive complexity stays within budget (Sonar S3776). None of
// these touch React state — they map inputs → display values.
// ---------------------------------------------------------------------------

// Resolved contact / company / cached counts. Project scope reads them off the
// BE preview; company scope derives them from the client-side contacts array.
const resolveCounts = ({ preview, contactCount, companyCount, contacts }) => {
  const cachedEmails = preview ? preview.cachedEmails ?? 0 : countCachedForField(contacts, 'email');
  const cachedPhones = preview ? preview.cachedPhones ?? 0 : countCachedForField(contacts, 'phone');
  return {
    resolvedContactCount: preview ? preview.contactCount ?? 0 : contactCount,
    resolvedCompanyCount: preview ? preview.companyCount ?? companyCount : companyCount,
    hasContactData: Array.isArray(contacts),
    cachedEmails,
    cachedPhones,
    hasCachedData: cachedEmails > 0 || cachedPhones > 0
  };
};

// Company-downloads (A/B) dimension. Project scope reads it off the precheck
// preview; company scope (TD-2283 item 2) gets the same A/B numbers via the
// dedicated `companyDownloads` prop. A null/uncapped limit means no box.
const resolveDownloads = (preview, companyDownloads) => {
  if (preview?.limit != null) {
    return {
      netNew: preview.netNew ?? 0,
      alreadyDownloaded: preview.alreadyDownloaded ?? 0,
      currentCount: preview.currentCount ?? 0,
      limit: preview.limit,
      have: Math.max(0, preview.limit - (preview.currentCount ?? 0)),
      allowed: preview.allowed !== false
    };
  }
  if (companyDownloads?.limit > 0) {
    return {
      netNew: companyDownloads.netNew ?? 0,
      alreadyDownloaded: companyDownloads.alreadyDownloaded ?? 0,
      currentCount: companyDownloads.used ?? 0,
      limit: companyDownloads.limit,
      have: Math.max(0, companyDownloads.limit - (companyDownloads.used ?? 0)),
      allowed: companyDownloads.allowed !== false
    };
  }
  return null;
};

// Per-field weighted cost, skip-aware. With contact data the cost honours Skip
// via computeRevealCost; without it (project scope) it is the contact count
// minus the cached rows (skip on) or the full count (skip off).
const computeFieldCost = ({
  hasContactData,
  contacts,
  skipPreviouslyEnriched,
  resolvedContactCount,
  cachedEmails,
  cachedPhones
}) => {
  if (hasContactData) {
    const costs = computeRevealCost(contacts, skipPreviouslyEnriched);
    return { email: costs.email, phone: costs.phone };
  }
  const billableEmails = skipPreviouslyEnriched
    ? Math.max(0, resolvedContactCount - cachedEmails)
    : resolvedContactCount;
  const billablePhones = skipPreviouslyEnriched
    ? Math.max(0, resolvedContactCount - cachedPhones)
    : resolvedContactCount;
  return { email: billableEmails * EMAIL_COST, phone: billablePhones * PHONE_COST };
};

const computeTotalCost = (selectedFields, fieldCost) =>
  selectedFields.reduce((sum, f) => sum + (fieldCost[f] ?? 0), 0);

// Role-bucket filter state (TD-2332). Options are the BE preview's FULL
// role_buckets breakdown (stable across toggles). Empty selection = all
// contacts, so an all-selected OR none-selected state is "no narrowing" — only
// a partial selection is an active filter.
//
// `unfilteredContactCount` is the contact total the connector captured from the
// first (unfiltered) preview. It is passed in rather than summed from
// `roleBuckets` because those counts no longer partition the contact set
// (TD-2956): someone who owns shares alongside an operative role counts under
// BOTH buckets, so the sum exceeds the number of contacts that exist.
const computeRoleState = (roleBuckets, selectedRoleBuckets, unfilteredContactCount) => {
  const hasRoleFilter = Array.isArray(roleBuckets) && roleBuckets.length > 0;
  const roleKeys = hasRoleFilter ? roleBuckets.map((b) => b.bucket) : [];
  const selectedRoleSet = new Set(selectedRoleBuckets);
  const selectedRoleCount = roleKeys.filter((k) => selectedRoleSet.has(k)).length;
  const allRolesSelected = roleKeys.length > 0 && selectedRoleCount === roleKeys.length;
  const someRolesSelected = selectedRoleCount > 0 && !allRolesSelected;
  return {
    hasRoleFilter,
    roleKeys,
    selectedRoleSet,
    allRolesSelected,
    someRolesSelected,
    roleFilterActive: someRolesSelected,
    // null when the connector has no unfiltered count to give (a failed or
    // in-flight first preview) — the label then omits the "of M" clause rather
    // than printing a total we cannot stand behind.
    roleTotal: Number.isFinite(unfilteredContactCount) ? unfilteredContactCount : null
  };
};

// Insufficiency + busy gates. Kept together so the boolean algebra lives in one
// small helper rather than in the component body.
const computeGates = ({
  hasSelectedFields,
  remaining,
  totalCost,
  downloads,
  isDownloading,
  isProject,
  previewLoading,
  phase
}) => {
  const creditsInsufficient = hasSelectedFields && remaining < totalCost;
  const downloadsInsufficient = !!downloads && !downloads.allowed;
  const insufficient = creditsInsufficient || downloadsInsufficient;
  const isBusy = isDownloading || (isProject && previewLoading);
  return {
    creditsInsufficient,
    downloadsInsufficient,
    insufficient,
    downloadBlocked: insufficient || isBusy,
    enrichmentInFlight: phase === 'submitting' || phase === 'pending'
  };
};

// Phase-aware Download-button label (ux-review fix #2 / TD-2271 / TD-2307).
const buildDownloadLabel = ({
  phase,
  totalContacts,
  enrichedCount,
  isDownloading,
  isProject,
  previewLoading,
  downloads,
  netNewDownloads,
  totalCost
}) => {
  if (phase === 'pending') {
    return totalContacts ? `Enriching… ${enrichedCount ?? 0} / ${totalContacts}` : 'Enriching…';
  }
  if (phase === 'submitting' || isDownloading) return 'Requesting…';
  if (isProject && previewLoading) return 'Checking…';
  if (downloads) return `Download · ${netNewDownloads} dl + ${totalCost} cr`;
  return `Download (${pluralCredits(totalCost)})`;
};

const buildDownloadTitle = ({
  downloadsInsufficient,
  creditsInsufficient,
  netNewDownloads,
  downloadsHave,
  totalCost,
  remaining,
  creditDeficit
}) => {
  if (downloadsInsufficient && creditsInsufficient) {
    return 'Insufficient downloads and credits for this export';
  }
  if (downloadsInsufficient) {
    return `Insufficient downloads: need ${netNewDownloads}, have ${downloadsHave}`;
  }
  if (creditsInsufficient) {
    return `Insufficient credits: need ${totalCost}, have ${remaining} (deficit ${creditDeficit})`;
  }
  return undefined;
};

// Insufficient banner (mockup state #4). Spells out the deficit across whichever
// dimension(s) fall short and the recovery path.
const buildInsufficientMsg = ({
  downloadsInsufficient,
  creditsInsufficient,
  netNewDownloads,
  downloadsHave,
  totalCost,
  remaining,
  creditDeficit
}) => {
  if (downloadsInsufficient && creditsInsufficient) {
    return (
      `Need ${netNewDownloads} downloads, have ${downloadsHave}. ` +
      `Need ${totalCost} credits, have ${remaining}. ` +
      'Reduce scope or contact sales to top up.'
    );
  }
  if (downloadsInsufficient) {
    return (
      `Need ${netNewDownloads} downloads, have ${downloadsHave}. ` +
      'Reduce scope or contact sales to top up.'
    );
  }
  if (creditsInsufficient) {
    return (
      `Need ${totalCost} credits, have ${remaining}. Shortfall: ${creditDeficit}. ` +
      'Reduce fields or untick to download base only.'
    );
  }
  return undefined;
};

const buildCachedParts = (cachedEmails, cachedPhones) => {
  const parts = [];
  if (cachedEmails > 0) parts.push(pluralUnit(cachedEmails, 'email', 'emails'));
  if (cachedPhones > 0) parts.push(pluralUnit(cachedPhones, 'phone', 'phones'));
  return parts.join(' + ');
};

// Count summary. Company: "N contacts". Project: "N contacts across M
// companies" — degrading to "Contacts across M companies" while unknown — plus
// the already-downloaded + cached context once the preview lands. TD-2332: an
// active role filter reads "N of M contacts" (N filtered, M full total).
// TD-2956: M comes from the unfiltered preview, so it is the number of contacts
// that exist; when it is unknown the clause is dropped and the label reads "N
// contacts", which is true under a filter even if it says less than we'd like.
const buildCountLabel = ({
  isProject,
  roleFilterActive,
  resolvedContactCount,
  roleTotal,
  resolvedCompanyCount,
  downloads,
  preview,
  hasCachedData,
  cachedEmails,
  cachedPhones,
  skipPreviouslyEnriched
}) => {
  const contactLabel = pluralUnit(resolvedContactCount, 'contact', 'contacts');
  const roleTotalUnit = roleTotal === 1 ? 'contact' : 'contacts';
  const filteredContactLabel =
    roleFilterActive && roleTotal != null
      ? `${resolvedContactCount} of ${roleTotal} ${roleTotalUnit}`
      : contactLabel;
  if (!isProject) return filteredContactLabel;

  const companyLabel = pluralUnit(resolvedCompanyCount, 'company', 'companies');
  let label =
    roleFilterActive || resolvedContactCount > 0
      ? `${filteredContactLabel} across ${companyLabel}`
      : `Contacts across ${companyLabel}`;
  if (downloads) label += ` · ${downloads.alreadyDownloaded} already downloaded`;
  if (preview && hasCachedData) {
    const cached = skipPreviouslyEnriched ? 'cached' : CACHED_CHARGED_AGAIN;
    label += ` · ${buildCachedParts(cachedEmails, cachedPhones)} ${cached}`;
  }
  return label;
};

// Item 10 cached indicator (mockup state #2) — company scope only. Project scope
// folds the cached context into the subheader above. Gated on the variant (not on
// preview presence) so it still shows for the company scope now that it too is
// preview-driven (TD-2527 role filter); the numbers below resolve from the preview
// when present, so they reflect any active role filter.
const buildCachedIndicator = ({
  isProject,
  hasCachedData,
  resolvedContactCount,
  cachedEmails,
  cachedPhones,
  skipPreviouslyEnriched
}) => {
  if (isProject || !hasCachedData) return null;
  const contactLabel = pluralUnit(resolvedContactCount, 'contact', 'contacts');
  const cached = skipPreviouslyEnriched ? 'cached, included' : CACHED_CHARGED_AGAIN;
  return `${contactLabel}: ${buildCachedParts(cachedEmails, cachedPhones)} ${cached}`;
};

// Skip-toggle subtitle (mockup states #2/#3).
const buildSkipSubtitle = ({ skipDisabled, skipPreviouslyEnriched }) => {
  if (skipDisabled) return 'Select a field to enable';
  if (skipPreviouslyEnriched) return 'Only charges for contacts without cached data';
  return 'Re-fetches every contact, even if already cached';
};

// ---------------------------------------------------------------------------
// Presentational sub-components. Each renders one slice of the dialog and
// self-gates so the parent's JSX stays flat.
// ---------------------------------------------------------------------------

// Company-downloads (A/B) stat box. While the precheck is in flight it renders
// a skeleton (headline + frame, blank value) so the value fills in place rather
// than the whole box popping in (TD-2283 review item 2).
const CompanyDownloadsBox = ({
  downloads,
  downloadsInsufficient,
  companyDownloadsLoading,
  isProject,
  previewLoading
}) => {
  if (downloads) {
    const boxColor = downloadsInsufficient
      ? 'grey'
      : toBoxColor(getUsageColor(downloads.currentCount, downloads.limit));
    return (
      <div
        className={`enrichment-credit-counter enrichment-credit-counter-${boxColor}`}
        role={downloadsInsufficient ? 'alert' : 'status'}
        aria-live={downloadsInsufficient ? 'assertive' : 'polite'}
      >
        <div className="enrichment-credit-counter-label">Company downloads</div>
        <div
          className="enrichment-credit-counter-value"
          title={`${downloads.currentCount} of ${downloads.limit} downloads used`}
        >
          {downloads.have} / {downloads.limit}
        </div>
      </div>
    );
  }
  if (companyDownloadsLoading || (isProject && previewLoading)) {
    return (
      <div className="enrichment-credit-counter enrichment-credit-counter-skeleton">
        <div className="enrichment-credit-counter-label">Company downloads</div>
        <div className="enrichment-credit-counter-value" aria-hidden="true">
          &nbsp;
        </div>
      </div>
    );
  }
  return null;
};

CompanyDownloadsBox.propTypes = {
  downloads: PropTypes.shape({
    currentCount: PropTypes.number,
    limit: PropTypes.number,
    have: PropTypes.number
  }),
  downloadsInsufficient: PropTypes.bool,
  companyDownloadsLoading: PropTypes.bool,
  isProject: PropTypes.bool,
  previewLoading: PropTypes.bool
};

// Best-effort notices shown above the fields: the fail-open precheck message
// (TD-2271) and the company-scope cached indicator (Item 10).
const DialogNotices = ({ isProject, previewFailed, cachedIndicator }) => (
  <>
    {isProject && previewFailed && (
      <output className="enrichment-download-dialog__count">
        Live download &amp; credit counts are unavailable right now — your limits are still enforced
        when you download.
      </output>
    )}
    {cachedIndicator && (
      <output className="enrichment-download-dialog__cached">{cachedIndicator}</output>
    )}
  </>
);

DialogNotices.propTypes = {
  isProject: PropTypes.bool,
  previewFailed: PropTypes.bool,
  cachedIndicator: PropTypes.string
};

// Role-bucket filter (TD-2332). Rendered only when the BE preview carries
// role_buckets. Row markup mirrors the enrichment-fields rows; the "Select all"
// master uses the indeterminate pattern from KeyContactsActionBar.
const RoleFilterSection = ({
  roleBuckets,
  selectedRoleBuckets,
  roleFilterLoading,
  onRoleBucketsChange
}) => {
  const { hasRoleFilter, roleKeys, selectedRoleSet, allRolesSelected, someRolesSelected } =
    computeRoleState(roleBuckets, selectedRoleBuckets);
  if (!hasRoleFilter) return null;

  const toggleAll = () => onRoleBucketsChange?.(allRolesSelected ? [] : roleKeys);
  const toggleBucket = (key) => {
    const next = selectedRoleSet.has(key)
      ? selectedRoleBuckets.filter((k) => k !== key)
      : [...selectedRoleBuckets, key];
    onRoleBucketsChange?.(next);
  };

  return (
    <section
      className={`enrichment-download-dialog__section enrichment-download-dialog__roles${
        roleFilterLoading ? ' is-loading' : ''
      }`}
    >
      <div className="enrichment-download-dialog__section-title">Filter by role</div>
      <label className="enrichment-download-dialog__field enrichment-download-dialog__role-all">
        <input
          type="checkbox"
          checked={allRolesSelected}
          ref={(el) => {
            if (el) el.indeterminate = someRolesSelected;
          }}
          onChange={toggleAll}
        />
        <span>{allRolesSelected ? 'Deselect all' : 'Select all'}</span>
      </label>
      {roleBuckets.map((b) => (
        <label key={b.bucket} className="enrichment-download-dialog__field">
          <input
            type="checkbox"
            checked={selectedRoleSet.has(b.bucket)}
            onChange={() => toggleBucket(b.bucket)}
          />
          <span>{b.label}</span>
          <span className="enrichment-download-dialog__field-cost">{b.count}</span>
        </label>
      ))}
    </section>
  );
};

RoleFilterSection.propTypes = {
  roleBuckets: PropTypes.arrayOf(
    PropTypes.shape({
      bucket: PropTypes.string,
      label: PropTypes.string,
      count: PropTypes.number
    })
  ),
  selectedRoleBuckets: PropTypes.arrayOf(PropTypes.string),
  roleFilterLoading: PropTypes.bool,
  onRoleBucketsChange: PropTypes.func
};

// Per-field enrichment checkboxes with live weighted line costs.
const EnrichmentFields = ({ fields, fieldCost, onToggle }) => {
  const lineCost = (key) => (fields[key] ? fieldCost[key] : 0);
  return (
    <section className="enrichment-download-dialog__section enrichment-download-dialog__enrichment">
      <div className="enrichment-download-dialog__section-title">Enrichment fields</div>
      <label className="enrichment-download-dialog__field">
        <input type="checkbox" checked={fields.email} onChange={() => onToggle('email')} />
        <span>Work Email</span>
        <span className="enrichment-download-dialog__field-cost">
          {pluralCredits(lineCost('email'))}
        </span>
      </label>
      <label className="enrichment-download-dialog__field">
        <input type="checkbox" checked={fields.phone} onChange={() => onToggle('phone')} />
        <span>Mobile phone</span>
        <span className="enrichment-download-dialog__field-cost">
          {pluralCredits(lineCost('phone'))}
        </span>
      </label>
    </section>
  );
};

EnrichmentFields.propTypes = {
  fields: PropTypes.shape({ email: PropTypes.bool, phone: PropTypes.bool }).isRequired,
  fieldCost: PropTypes.shape({ email: PropTypes.number, phone: PropTypes.number }).isRequired,
  onToggle: PropTypes.func.isRequired
};

// Skip-previously-enriched toggle + subtitle, plus the re-use hint (kept visible
// whenever there is cached data, and the primary cached message when per-contact
// data isn't available to compute exact counts).
const SkipToggle = ({
  skipPreviouslyEnriched,
  skipDisabled,
  skipSubtitle,
  skipDescriptionId,
  hasCachedData,
  hasContactData,
  onToggle
}) => {
  const showReuseHint = skipPreviouslyEnriched && (hasCachedData || !hasContactData);
  return (
    <>
      <label // NOSONAR (S6853): the wrapped <input> derives its accessible name from the label text ("Skip previously enriched") via the accname algorithm; S6853's static depth check can't trace text nested two spans deep. Subtitle linked via aria-describedby.
        className={`enrichment-download-dialog__skip-toggle${skipDisabled ? ' is-disabled' : ''}`}
      >
        <input
          type="checkbox"
          checked={skipPreviouslyEnriched}
          disabled={skipDisabled}
          onChange={onToggle}
          aria-describedby={skipDescriptionId}
        />
        <span className="enrichment-download-dialog__skip-text">
          <span className="enrichment-download-dialog__skip-label">Skip previously enriched</span>
          <span id={skipDescriptionId} className="enrichment-download-dialog__skip-subtitle">
            {skipSubtitle}
          </span>
        </span>
      </label>
      {showReuseHint && (
        <p className="enrichment-download-dialog__skip-hint">
          Contacts already enriched are re-used at no extra credit cost.
        </p>
      )}
    </>
  );
};

SkipToggle.propTypes = {
  skipPreviouslyEnriched: PropTypes.bool,
  skipDisabled: PropTypes.bool,
  skipSubtitle: PropTypes.string,
  skipDescriptionId: PropTypes.string,
  hasCachedData: PropTypes.bool,
  hasContactData: PropTypes.bool,
  onToggle: PropTypes.func.isRequired
};

// Cancel + Download footer. Insufficient/in-flight treatment is aria-disabled +
// click guard (guards live in the parent handlers) + native title + sr-only
// message linked via aria-describedby.
const DialogFooter = ({
  enrichmentInFlight,
  downloadBlocked,
  insufficient,
  downloadTitle,
  downloadLabel,
  onCancel,
  onDownload
}) => (
  <div className="enrichment-download-dialog__footer">
    <button
      type="button"
      className={`enrichment-download-dialog__btn enrichment-download-dialog__btn--cancel${
        enrichmentInFlight ? ' is-disabled' : ''
      }`}
      aria-disabled={enrichmentInFlight ? 'true' : undefined}
      title={enrichmentInFlight ? 'Enrichment in progress — this can’t be cancelled' : undefined}
      onClick={onCancel}
    >
      Cancel
    </button>
    <button
      type="button"
      className={`enrichment-download-dialog__btn enrichment-download-dialog__btn--download${
        downloadBlocked ? ' is-on-hold' : ''
      }`}
      aria-disabled={downloadBlocked ? 'true' : undefined}
      aria-describedby={insufficient ? INSUFFICIENT_MSG_ID : undefined}
      title={downloadTitle}
      onClick={onDownload}
    >
      {downloadLabel}
    </button>
  </div>
);

DialogFooter.propTypes = {
  enrichmentInFlight: PropTypes.bool,
  downloadBlocked: PropTypes.bool,
  insufficient: PropTypes.bool,
  downloadTitle: PropTypes.string,
  downloadLabel: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired
};

/**
 * EnrichmentDownloadDialog (AC-7, v3 delta #3 + Addendum v3 redesign TD-2263 #7/#10).
 *
 * Replaces the v2.1 dialog (mode radio + cost preview footer + overage modal
 * — all removed per Hamid's Addendum v3, 2026-05-22).
 *
 * Per-field cost is weighted (Work Email = 1 credit, Mobile phone = 10 credits,
 * from keyContactsCostUtils) and live: with the `contacts` array (company scope)
 * the weighted sum honours the Skip toggle via computeRevealCost; without it
 * (project scope) the cost is driven by the BE preview's `contactCount` minus
 * the cached email / phone rows (TD-2270), so the project cost is skip-aware too.
 *
 * TD-2271 — merged download dialog: when `downloadPreview` is supplied (project
 * scope) the header shows BOTH cost dimensions — Company downloads (A/B) and
 * Contact credits (X/Y) — and the Download button reads "Download · {dl} dl +
 * {cr} cr". TD-2332 adds an optional "Filter by role" section.
 *
 * Insufficient treatment (mirrors the ContactCard reveal-button pattern):
 *   aria-disabled + click guard + native `title` tooltip + sr-only message
 * — WCAG-compliant; the disabled button stays focusable so the deficit copy is
 * reachable by keyboard / SR users, linked via `aria-describedby`.
 *
 * Focus management / a11y is delegated to the shared `useModalA11y` hook
 * (escape-to-close, ref-counted body scroll-lock, initial focus, focus trap,
 * restore-on-close) — the mandated convention, cloned from FallbackActivationModal
 * (Radix Portal + dialog ref + tabIndex={-1}).
 *
 * The heavy derivation logic lives in the pure helpers above; this component
 * wires state → helpers → presentational sub-components.
 */
const EnrichmentDownloadDialog = observer(function EnrichmentDownloadDialog({
  isOpen,
  onClose,
  onDownload,
  contactCount = 0,
  companyCount = 0,
  contacts = null,
  variant = 'company',
  isDownloading = false,
  phase = 'idle',
  enrichedCount,
  totalContacts,
  downloadPreview = null,
  previewLoading = false,
  previewFailed = false,
  companyDownloads = null,
  companyDownloadsLoading = false,
  roleBuckets = null,
  roleTotal = null,
  selectedRoleBuckets = [],
  onRoleBucketsChange,
  roleFilterLoading = false
}) {
  // Shared snapshot: a background load is a no-op once any page consumer has
  // already loaded status, so gating on isOpen saves nothing here.
  const { remaining } = useEnrichmentAccess();
  const [fields, setFields] = useState({ email: false, phone: false });
  const [skipPreviouslyEnriched, setSkipPreviouslyEnriched] = useState(true);
  const skipDescriptionId = useId();

  const dialogRef = useRef(null);
  useModalA11y({ isOpen, onClose, dialogRef });

  // Reset internal state whenever the dialog is re-opened. Otherwise a user who
  // cancels mid-selection finds their old picks on the next open.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setFields({ email: false, phone: false });
      setSkipPreviouslyEnriched(true);
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  const selectedFields = useMemo(
    () =>
      Object.entries(fields)
        .filter(([, v]) => v)
        .map(([k]) => k),
    [fields]
  );

  const isProject = variant === 'project';
  const preview = downloadPreview;

  const { resolvedContactCount, resolvedCompanyCount, hasContactData, cachedEmails, cachedPhones, hasCachedData } =
    resolveCounts({ preview, contactCount, companyCount, contacts });

  const fieldCost = useMemo(
    () =>
      computeFieldCost({
        hasContactData,
        contacts,
        skipPreviouslyEnriched,
        resolvedContactCount,
        cachedEmails,
        cachedPhones
      }),
    [hasContactData, contacts, skipPreviouslyEnriched, resolvedContactCount, cachedEmails, cachedPhones]
  );

  const totalCost = computeTotalCost(selectedFields, fieldCost);
  const role = computeRoleState(roleBuckets, selectedRoleBuckets, roleTotal);

  if (!isOpen) return null;

  const downloads = resolveDownloads(preview, companyDownloads);
  const netNewDownloads = downloads?.netNew ?? 0;
  const { creditsInsufficient, downloadsInsufficient, insufficient, downloadBlocked, enrichmentInFlight } =
    computeGates({
      hasSelectedFields: selectedFields.length > 0,
      remaining,
      totalCost,
      downloads,
      isDownloading,
      isProject,
      previewLoading,
      phase
    });
  const creditDeficit = totalCost - remaining;
  const skipDisabled = selectedFields.length === 0;
  const effectiveRoleBuckets = role.roleFilterActive ? selectedRoleBuckets : [];

  const toggleField = (key) => setFields((prev) => ({ ...prev, [key]: !prev[key] }));
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) onClose?.();
  };
  const handleDownloadClick = () => {
    if (downloadBlocked) return;
    onDownload({ enrichmentFields: selectedFields, skipPreviouslyEnriched, roleBuckets: effectiveRoleBuckets });
  };
  const handleCancelClick = () => {
    if (enrichmentInFlight) return;
    onClose();
  };

  const deficitArgs = {
    downloadsInsufficient,
    creditsInsufficient,
    netNewDownloads,
    downloadsHave: downloads?.have,
    totalCost,
    remaining,
    creditDeficit
  };

  const title = isProject ? 'Download contact list as XLSX' : 'Download contacts as XLSX';
  const countLabel = buildCountLabel({
    isProject,
    roleFilterActive: role.roleFilterActive,
    resolvedContactCount,
    roleTotal: role.roleTotal,
    resolvedCompanyCount,
    downloads,
    preview,
    hasCachedData,
    cachedEmails,
    cachedPhones,
    skipPreviouslyEnriched
  });
  const downloadLabel = buildDownloadLabel({
    phase,
    totalContacts,
    enrichedCount,
    isDownloading,
    isProject,
    previewLoading,
    downloads,
    netNewDownloads,
    totalCost
  });
  const cachedIndicator = buildCachedIndicator({
    isProject,
    hasCachedData,
    resolvedContactCount,
    cachedEmails,
    cachedPhones,
    skipPreviouslyEnriched
  });

  return (
    <Portal.Root>
      <div
        className="enrichment-download-dialog-overlay"
        role="presentation" // NOSONAR (S6819): decorative backdrop; role marks it non-interactive to AT. Click-to-close is a mouse convenience — keyboard close is Escape via useModalA11y. Mirrors SessionLimitModal.
        onClick={handleBackdropClick}
      >
        <div
          ref={dialogRef}
          className={`enrichment-download-dialog${insufficient ? ' is-insufficient' : ''}`}
          role="dialog" // NOSONAR (S6819): intentional custom modal — native <dialog>/showModal regresses focus/backdrop/top-layer, and inner landmarks would inherit global header/footer CSS (TD-2263 #7, see comment below). Uses the useModalA11y legacy path.
          aria-modal="true"
          aria-labelledby={TITLE_ID}
          tabIndex={-1}
        >
          {/* Plain <div>, not a semantic <header>/<footer>: the app's global
              `header {}` / `footer {}` element rules (footer.less / header.less)
              set @primary-black bg, 80px height + page padding, which bleed into
              a dialog's semantic landmarks (TD-2263 #7). role=dialog needs no
              inner landmarks. */}
          <div className="enrichment-download-dialog__header">
            <div className="enrichment-download-dialog__heading">
              <h2 id={TITLE_ID} className="enrichment-download-dialog__title">
                {title}
              </h2>
              <p className="enrichment-download-dialog__count">{countLabel}</p>
            </div>
          </div>

          <div className="enrichment-download-dialog__stats">
            <CompanyDownloadsBox
              downloads={downloads}
              downloadsInsufficient={downloadsInsufficient}
              companyDownloadsLoading={companyDownloadsLoading}
              isProject={isProject}
              previewLoading={previewLoading}
            />
            <EnrichmentCreditCounter variant="row" insufficient={creditsInsufficient} />
          </div>

          <DialogNotices
            isProject={isProject}
            previewFailed={previewFailed}
            cachedIndicator={cachedIndicator}
          />

          <RoleFilterSection
            roleBuckets={roleBuckets}
            selectedRoleBuckets={selectedRoleBuckets}
            roleFilterLoading={roleFilterLoading}
            onRoleBucketsChange={onRoleBucketsChange}
          />

          <EnrichmentFields fields={fields} fieldCost={fieldCost} onToggle={toggleField} />

          <SkipToggle
            skipPreviouslyEnriched={skipPreviouslyEnriched}
            skipDisabled={skipDisabled}
            skipSubtitle={buildSkipSubtitle({ skipDisabled, skipPreviouslyEnriched })}
            skipDescriptionId={skipDescriptionId}
            hasCachedData={hasCachedData}
            hasContactData={hasContactData}
            onToggle={() => setSkipPreviouslyEnriched((s) => !s)}
          />

          {insufficient && (
            // Mockup state #4: banner replaces the footer hint. <output> carries
            // an implicit role=status so AT announce it on appearance.
            <output
              id={INSUFFICIENT_MSG_ID}
              className="enrichment-download-dialog__insufficient-banner"
            >
              {buildInsufficientMsg(deficitArgs)}
            </output>
          )}

          <DialogFooter
            enrichmentInFlight={enrichmentInFlight}
            downloadBlocked={downloadBlocked}
            insufficient={insufficient}
            downloadTitle={buildDownloadTitle(deficitArgs)}
            downloadLabel={downloadLabel}
            onCancel={handleCancelClick}
            onDownload={handleDownloadClick}
          />
        </div>
      </div>
    </Portal.Root>
  );
});

EnrichmentDownloadDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  contactCount: PropTypes.number,
  companyCount: PropTypes.number,
  // Optional per-contact enrichment status (company scope). Same shape as
  // KeyContactsActionBar consumes; absent in project scope (search results
  // don't load contact-level data): the dialog degrades gracefully.
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      enrichment_status_email: PropTypes.string,
      enrichment_status_phone: PropTypes.string
    })
  ),
  variant: PropTypes.oneOf(['company', 'project']),
  isDownloading: PropTypes.bool,
  phase: PropTypes.oneOf(['idle', 'submitting', 'pending', 'downloaded', 'error']),
  // Live enrichment progress for the `pending` label (TD-2307). Sourced from the
  // status poll via useEnrichmentContactsDownload; both undefined until the first
  // poll lands → label falls back to plain "Enriching…".
  enrichedCount: PropTypes.number,
  totalContacts: PropTypes.number,
  // Project-scope merged-dialog inputs (TD-2270 / TD-2271), from
  // POST /bulk-download-precheck?includeContacts, normalised to camelCase.
  downloadPreview: PropTypes.shape({
    contactCount: PropTypes.number,
    companyCount: PropTypes.number,
    alreadyDownloaded: PropTypes.number,
    netNew: PropTypes.number,
    currentCount: PropTypes.number,
    limit: PropTypes.number,
    remainingAfter: PropTypes.number,
    allowed: PropTypes.bool,
    cachedEmails: PropTypes.number,
    cachedPhones: PropTypes.number
  }),
  previewLoading: PropTypes.bool,
  previewFailed: PropTypes.bool,
  // Company-scope company-downloads (A/B) counter (TD-2283 item 2). Separate
  // from downloadPreview so the company variant's contact/cached derivations
  // (keyed off `preview` truthiness) are untouched. `used`/`limit` are the
  // global download tracker; `netNew` is 1 (new company) or 0 (already
  // downloaded); `allowed` drives the same at-limit gating as the project box.
  companyDownloads: PropTypes.shape({
    used: PropTypes.number,
    limit: PropTypes.number,
    netNew: PropTypes.number,
    alreadyDownloaded: PropTypes.number,
    allowed: PropTypes.bool
  }),
  // Company-scope precheck in flight — render the downloads box as a skeleton
  // (headline + frame, blank value) so the value fills in place (TD-2283 item 2).
  companyDownloadsLoading: PropTypes.bool,
  // Role-bucket filter (TD-2332). `roleBuckets` is the BE preview's FULL
  // role_buckets breakdown (options + counts, stable across toggles); rendering
  // the "Filter by role" section is gated on it being non-empty. Selection is
  // controlled: `selectedRoleBuckets` holds the checked bucket keys and
  // `onRoleBucketsChange(nextKeys)` bubbles changes to the connector, which
  // re-fetches the preview + threads the keys to the export. Empty = all.
  roleBuckets: PropTypes.arrayOf(
    PropTypes.shape({
      bucket: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number
    })
  ),
  // TD-2956: the contact total from the FIRST (unfiltered) preview, used as the
  // denominator in "N of M contacts". Not derivable from `roleBuckets` above —
  // a shareholder who also manages is counted in two buckets, so those counts
  // sum to more than the company has contacts. Null = unknown, clause omitted.
  roleTotal: PropTypes.number,
  selectedRoleBuckets: PropTypes.arrayOf(PropTypes.string),
  onRoleBucketsChange: PropTypes.func,
  // Role-filter re-fetch in flight (counts refreshing) — dims the section.
  roleFilterLoading: PropTypes.bool
};

export default EnrichmentDownloadDialog;
